import { Button, Card, CardContent, CardFooter, CardHeader, Icon } from "@/shared/components";
import { useNavigate, useRouteError } from "react-router";

interface RouterError {
    statusText?: string;
    message?: string;
    status?: number;
    stack?: string;
}

/**
 * ErrorPage displays error information for route errors, including status, message, and stack trace if available.
 * @returns {JSX.Element}
 */
export default function ErrorPage() {
    const error = useRouteError() as RouterError | Error;
    const navigate = useNavigate();

    const errorMessage = (error as RouterError).statusText ?? error.message ?? "An unknown error occurred";
    const errorStatus = (error as RouterError).status ?? 500;
    const errorStack = (error as RouterError).stack ?? (error as Error).stack;

    return (
        <div className="bg-background/95 flex min-h-screen w-full items-center justify-center p-4">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader>
                    <div className="flex flex-col items-center space-y-4">
                        <div className="bg-destructive/10 flex h-12 w-12 items-center justify-center rounded-full">
                            <Icon name="error" className="text-destructive h-6 w-6" />
                        </div>
                        <div className="space-y-1 text-center">
                            <h1 className="text-xxxl font-bold tracking-tight">Error {errorStatus}</h1>
                            <p className="text-muted-foreground">Oops! Something went wrong.</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4 text-center">
                        <p className="text-muted-foreground bg-muted/50 text-s rounded-lg border p-3">{errorMessage}</p>
                        {errorStack && (
                            <pre className="bg-muted/30 mt-4 max-h-48 overflow-x-auto rounded p-2 text-left text-xs whitespace-pre-wrap">
                                {errorStack}
                            </pre>
                        )}
                    </div>
                </CardContent>
                <CardFooter className="flex justify-center">
                    <Button variant="primary" onClick={() => navigate(-1)} className="w-full sm:w-auto">
                        Go Back
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
